import type { Anchor, AppSettings, BookMeta, Chapter, ChatScope, Conversation, Highlight, Note } from '../shared/types';
import { api, AuthError, onUnauthorized, setToken, type BookDetail } from './api';
import { initSettings } from './settings';
import { initReader, jumpToAnchor, renderAll, scrollToBlock, type SelectionInfo } from './reader';
import {
  appendAssistantPending,
  appendLocalUserMessage,
  beginAssistantBubble,
  focusChatInput,
  focusNoteInput,
  initCompanion,
  renderMessages,
  renderModelSelect,
  renderNotes,
  setQuote,
} from './companion';
import { initShelf, renderShelf, renderToc } from './shelf';
import { toast } from './toast';
import { initModelManager } from './model-manager';
import { connectionOf } from './models';
import { closeDrawer } from './drawers';

interface State {
  books: BookMeta[];
  book: BookDetail | null;
  chapter: Chapter | null;
  chapterIdx: number;
  highlights: Highlight[];
  notes: Note[];
  noteAnchor: Anchor | null;
  settings: AppSettings | null;
  model: string;
  chat: Conversation | null;
  scope: ChatScope;
}

const state: State = {
  books: [],
  book: null,
  chapter: null,
  chapterIdx: 0,
  highlights: [],
  notes: [],
  noteAnchor: null,
  settings: null,
  model: '',
  chat: null,
  scope: 'selection',
};

const LAST_BOOK_KEY = 'margin.lastBook';
const LAST_MODEL_KEY = 'margin.lastModel';
let chapterLoadVersion = 0;
let chatLoadVersion = 0;

function el(id: string): HTMLElement {
  return document.getElementById(id)!;
}

// ---------- 章节与书籍加载 ----------

async function loadChapter(index: number, blockId?: string | null) {
  const book = state.book;
  if (!book || index < 0 || index >= book.chapters.length) return;
  const version = ++chapterLoadVersion;
  try {
    const chapter = await api.getChapter(book.id, book.chapters[index]!.id);
    if (version !== chapterLoadVersion || state.book !== book) return;
    state.chapterIdx = index;
    state.chapter = chapter;
    api.savePosition(book.id, chapter.id, blockId ?? null).catch(() => {});
    renderAll();
    el('page').scrollTop = 0;
    if (blockId) scrollToBlock(blockId);
    renderToc();
    closeDrawer('shelf', { focusReader: true });
  } catch (error) {
    if (version !== chapterLoadVersion || state.book !== book || error instanceof AuthError) return;
    toast(error instanceof Error ? error.message : '加载章节失败');
  }
}

/** 加载当前模型与本书的对话 */
async function loadChat() {
  const version = ++chatLoadVersion;
  const bookId = state.book?.id;
  const model = state.model;
  if (!bookId || !model) {
    state.chat = null;
    renderMessages();
    return;
  }
  state.chat = null;
  renderMessages();
  try {
    const conversations = await api.getChats(bookId, model);
    if (version !== chatLoadVersion || state.book?.id !== bookId || state.model !== model) return;
    state.chat = conversations[0] ?? null;
  } catch {
    if (version !== chatLoadVersion || state.book?.id !== bookId || state.model !== model) return;
    state.chat = null;
  }
  renderMessages();
}

async function sendChat(text: string, scope: ChatScope = state.scope) {
  if (!state.book) {
    toast('先打开一本书');
    return;
  }
  // 无选段时 selection 自动降级为 chapter
  const useScope = scope === 'selection' && !state.noteAnchor ? 'chapter' : scope;
  const anchor = useScope === 'selection' ? state.noteAnchor : null;
  const modelInfo = state.settings?.models.find((m) => m.id === state.model);
  if (!modelInfo || modelInfo.archived) {
    toast(modelInfo?.archived ? '请先恢复这个实例，或选择其他实例' : '先添加一个共读实例');
    return;
  }
  const bookId = state.book.id;
  const model = state.model;
  const stillCurrent = () => state.book?.id === bookId && state.model === model;
  if (connectionOf(modelInfo) === 'api' && !modelInfo.hasProvider) {
    toast('请先在“管理”中完成这个实例的 API 配置');
    return;
  }

  // 未配置 provider：只把问题留下来（旧占位行为）
  if (connectionOf(modelInfo) !== 'api') {
    try {
      const chat = await api.postChat(bookId, { model, text, anchor, scope: useScope });
      if (!stillCurrent()) return;
      state.chat = chat;
      renderMessages();
      appendAssistantPending();
    } catch (error) {
      if (error instanceof AuthError) return;
      toast(error instanceof Error ? error.message : '发送失败');
    }
    return;
  }

  // 流式对话：乐观渲染 user 气泡 + 空 assistant 气泡，逐段填充
  appendLocalUserMessage(text, anchor);
  const bubble = beginAssistantBubble();
  try {
    await api.streamChat(bookId, { model, text, anchor, scope: useScope }, {
      onDelta: (delta) => { if (stillCurrent()) bubble.append(delta); },
      onDone: () => {
        bubble.finish();
        if (stillCurrent()) void refreshChat();
      },
      onError: (message) => bubble.fail(`出错了：${message}`),
    });
  } catch (error) {
    if (error instanceof AuthError) return;
    bubble.fail(error instanceof Error ? error.message : '发送失败');
  }
}

/** 对话结束后以服务端持久化数据重绘消息流 */
async function refreshChat() {
  const bookId = state.book?.id;
  const model = state.model;
  if (!bookId || !model) return;
  const version = ++chatLoadVersion;
  try {
    const conversations = await api.getChats(bookId, model);
    if (version !== chatLoadVersion || state.book?.id !== bookId || state.model !== model) return;
    state.chat = conversations[0] ?? null;
    renderMessages();
  } catch {
    // 刷新失败不打断阅读，下次开书/切模型会再拉
  }
}

async function openBook(id: string) {
  try {
    state.book = await api.getBook(id);
    [state.highlights, state.notes] = await Promise.all([api.getHighlights(id), api.getNotes(id)]);
    const position = await api.getPosition(id);
    const savedIdx = position ? state.book.chapters.findIndex((c) => c.id === position.chapterId) : -1;
    localStorage.setItem(LAST_BOOK_KEY, id);
    state.noteAnchor = null;
    setQuote(null);
    renderNotes();
    renderShelf();
    await Promise.all([
      loadChat(),
      loadChapter(savedIdx >= 0 ? savedIdx : 0, savedIdx >= 0 ? position!.blockId : null),
    ]);
  } catch (error) {
    if (error instanceof AuthError) return;
    toast(error instanceof Error ? error.message : '打开书籍失败');
  }
}

// ---------- 初始化 ----------

initSettings();

initReader({
  getBook: () => state.book,
  getChapter: () => state.chapter,
  getHighlights: () => state.highlights,
  getNotes: () => state.notes,
  chapterIndex: () => state.chapterIdx,
  goChapter: (index, blockId) => loadChapter(index, blockId),
  savePosition: (blockId) => {
    if (!state.book || !state.chapter) return;
    api.savePosition(state.book.id, state.chapter.id, blockId).catch(() => {});
  },
  addHighlight: async (anchor) => {
    const highlight = await api.addHighlight(state.book!.id, anchor);
    state.highlights.push(highlight);
    renderNotes();
    toast('已划线');
  },
  removeHighlight: async (id) => {
    await api.deleteHighlight(state.book!.id, id);
    state.highlights = state.highlights.filter((h) => h.id !== id);
    renderNotes();
  },
  onAsk: (sel: SelectionInfo) => {
    state.noteAnchor = sel.anchor;
    setQuote(sel.text);
    if (state.book && state.chapter) {
      api
        .pushSelection({ bookId: state.book.id, chapterId: state.chapter.id, text: sel.text, anchor: sel.anchor })
        .catch(() => {});
    }
    focusChatInput();
  },
  onAnnotate: (anchor, quote) => {
    state.noteAnchor = anchor;
    setQuote(quote);
    focusNoteInput();
  },
  authorName: (author) =>
    author === 'me' ? '我' : state.settings?.models.find((m) => m.id === author)?.name ?? author,
});

const CHIP_QUESTION: Record<'explain' | 'context', string> = {
  explain: '帮我解释这段选文。',
  context: '这段话和前文有什么联系？',
};

initCompanion({
  getNotes: () => state.notes,
  getHighlightCount: () => state.highlights.length,
  addNote: async (text) => {
    if (!state.book) {
      toast('先打开一本书');
      return;
    }
    const note = await api.addNote(state.book.id, state.noteAnchor, text);
    state.notes.unshift(note);
    renderNotes();
    renderAll(); // 刷新批注圆点
    toast('批注已保存');
  },
  deleteNote: async (id) => {
    if (!state.book) return;
    await api.deleteNote(state.book.id, id);
    state.notes = state.notes.filter((n) => n.id !== id);
    renderNotes();
    renderAll();
  },
  jumpToAnchor,
  getModels: () => state.settings?.models ?? [],
  getModel: () => state.model,
  setModel: (id) => {
    if (id === state.model) return;
    state.model = id;
    localStorage.setItem(LAST_MODEL_KEY, id);
    void loadChat();
  },
  getMessages: () => state.chat?.messages ?? [],
  getScope: () => state.scope,
  setScope: (scope) => {
    state.scope = scope;
  },
  sendChat: (text) => sendChat(text),
  refreshChat,
  askSelection: (action) => {
    if (!state.noteAnchor) {
      toast('先在正文中选中一段文字');
      return;
    }
    void sendChat(CHIP_QUESTION[action], 'selection');
  },
  getLocation: () =>
    state.book && state.chapter ? { bookTitle: state.book.title, chapterTitle: state.chapter.title } : null,
});

initModelManager({
  getSettings: () => state.settings,
  getModel: () => state.model,
  onSaved: async (settings, preferredId) => {
    state.settings = settings;
    state.model = preferredId ?? (settings.models.some((model) => model.id === state.model)
      ? state.model : settings.defaultModel || settings.models[0]?.id || '');
    localStorage.setItem(LAST_MODEL_KEY, state.model);
    renderModelSelect();
    renderNotes();
    renderAll();
    await loadChat();
  },
});

initShelf({
  getBooks: () => state.books,
  getBook: () => state.book,
  getChapterId: () => state.chapter?.id ?? null,
  openBook,
  openChapterById: (id) => {
    const index = state.book?.chapters.findIndex((c) => c.id === id) ?? -1;
    if (index >= 0) void loadChapter(index);
  },
  removeBook: async (id) => {
    await api.deleteBook(id);
    state.books = state.books.filter((b) => b.id !== id);
    if (state.book?.id === id) {
      state.book = null;
      state.chapter = null;
      state.highlights = [];
      state.notes = [];
      state.chat = null;
      setQuote(null);
      renderNotes();
      renderMessages();
      renderAll();
    }
    renderShelf();
    toast('已移除');
  },
  importFile: async (file) => {
    const base64 = await readAsBase64(file);
    const meta = await api.importBook(file.name, base64);
    state.books.unshift(meta);
    renderShelf();
    toast(`《${meta.title}》已导入`);
    await openBook(meta.id);
  },
});

// ---------- 备份与恢复 ----------

el('backupBtn').addEventListener('click', () => {
  void api.exportBackup().catch((error) => {
    if (error instanceof AuthError) return;
    toast(error instanceof Error ? error.message : '备份失败');
  });
});

const restoreInput = el('restoreInput') as HTMLInputElement;
el('restoreBtn').addEventListener('click', () => restoreInput.click());
restoreInput.addEventListener('change', () => {
  void (async () => {
    const file = restoreInput.files?.[0];
    restoreInput.value = '';
    if (!file) return;
    let backup: unknown;
    try {
      backup = JSON.parse(await file.text());
    } catch {
      toast('备份文件不是合法 JSON');
      return;
    }
    try {
      const preview = await api.restoreBackup(backup, false);
      const ok = window.confirm(
        `将覆盖当前全部数据：${preview.books} 本书 · ${preview.highlights} 条划线 · ${preview.notes} 条批注 · ${preview.conversations} 段对话。\n确定恢复？`,
      );
      if (!ok) return;
      const result = await api.restoreBackup(backup, true);
      toast(`已恢复：${result.books} 本书 · ${result.notes} 条批注`);
      // 重置本地状态后重新初始化
      state.book = null;
      state.chapter = null;
      state.chat = null;
      state.notes = [];
      state.highlights = [];
      await boot();
    } catch (error) {
      if (error instanceof AuthError) return;
      toast(error instanceof Error ? error.message : '恢复失败');
    }
  })();
});

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]!);
    reader.onerror = () => reject(new Error('读取文件失败'));
    reader.readAsDataURL(file);
  });
}

// ---------- 启动 ----------

function showAuthMask() {
  closeDrawer(undefined, { restoreFocus: false });
  const modelDialog = el('modelDialog') as HTMLDialogElement;
  if (modelDialog.open) modelDialog.close();
  el('authMask').hidden = false;
  (el('authToken') as HTMLInputElement).focus();
}

onUnauthorized(showAuthMask);

function submitToken() {
  const input = el('authToken') as HTMLInputElement;
  const token = input.value.trim();
  if (!token) return;
  setToken(token);
  input.value = '';
  el('authMask').hidden = true;
  void boot();
}

el('authSubmit').addEventListener('click', submitToken);
el('authToken').addEventListener('keydown', (e) => {
  if (e.key === 'Enter') submitToken();
});

async function boot() {
  try {
    state.settings = await api.getSettings();
    const lastModel = localStorage.getItem(LAST_MODEL_KEY);
    state.model = state.settings.models.some((m) => m.id === lastModel)
      ? lastModel! : state.settings.defaultModel || state.settings.models[0]?.id || '';
    renderModelSelect();

    state.books = await api.listBooks();
    renderShelf();
    const last = localStorage.getItem(LAST_BOOK_KEY);
    const first = state.books.find((b) => b.id === last) ?? state.books[0];
    if (first) await openBook(first.id);
    else renderAll();
  } catch (error) {
    if (error instanceof AuthError) return; // 遮罩已弹出，等令牌输入后重试
    toast('无法连接 Margin 服务，请确认服务已启动');
  }
}

void boot();
